import {firebaseClientSettings} from './backend/firebaseClientSettings'
import {createSessionService} from './backend/createSessionService'
import type {AuthService, CloudFunctionsService} from './types/services'
import type {Schedule, UserProfile} from './types/domain'
import {initializeApp} from 'firebase/app'
import {getAuth, setPersistence, signInWithPopup,
  GoogleAuthProvider, connectAuthEmulator, browserLocalPersistence, onAuthStateChanged,
  signOut as firebaseSignOut
} from 'firebase/auth'
import { collection, getFirestore, doc, getDoc, connectFirestoreEmulator, getDocs } from 'firebase/firestore'
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions'

import {createCloudFunctionsService} from './backend/createCloudFunctionsService'

export const GOOGLE_SIGN_IN = 'google'

const googleAuthProvider = new GoogleAuthProvider()
const {options, name, useEmulators} = firebaseClientSettings(import.meta.env)
const firebaseApp = initializeApp(options, name)
const auth = getAuth(firebaseApp)
const firestore = getFirestore(firebaseApp)
const functions = getFunctions(firebaseApp)

if (useEmulators) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099')
  connectFirestoreEmulator(firestore, '127.0.0.1', 8080)
  connectFunctionsEmulator(functions, '127.0.0.1', 5001)
}

export const cloudFunctionsService = createCloudFunctionsService((name, data) =>
  httpsCallable(functions, name)(data))

const getUserId = () => {
  const uid = auth.currentUser?.uid
  if (!uid) throw new Error('Sign in to perform this action.')
  return uid
}

const getUserProfile = async () => {
  const userRef = doc(firestore, 'users', getUserId())
  const userSnapshot = await getDoc(userRef)
  if (userSnapshot.exists()) {
    return userSnapshot.data() as UserProfile
  }
}

export const signOut = () => {
  return firebaseSignOut(auth)
}

export const signIn = async (providerMethod = GOOGLE_SIGN_IN, service: CloudFunctionsService = cloudFunctionsService) => {
  let provider
  switch (providerMethod) {
    case GOOGLE_SIGN_IN: {
      provider = googleAuthProvider
      break
    }
    default: {
      return
    }
  }
  await setPersistence(auth, browserLocalPersistence)
  try {
    await signInWithPopup(auth, provider)
    const userProfile = await getUserProfile()
    if (userProfile) {
      return userProfile
    } else {
      return service.createUser()
    }
  } catch (error) {
    const code = error instanceof Error && 'code' in error ? error.code : undefined
    if (code !== 'auth/popup-closed-by-user') {
      if (error instanceof Error && error.message.includes('has not been invited')) {
        console.warn('User not invited')
      } else {
        console.error(error)
        console.log(`error.code: ${code}`)
        console.log(`error.message: ${error instanceof Error ? error.message : String(error)}`)
      }
    }
  }
}

export const getSchedules = async () => {
  const querySnapshot = await getDocs(collection(firestore, `/users/${getUserId()}/schedules`))
  const schedules: Schedule[] = []
  querySnapshot.forEach((doc) => {
    schedules.push(doc.data() as Schedule)
  })
  return schedules
}

export const authService: AuthService = {
  subscribe: listener => onAuthStateChanged(auth, listener),
  signIn,
  signOut,
}

export const scheduleRepository = {getSchedules}

export const sessionService = createSessionService((name, data) => httpsCallable(functions, name)(data))
